import {
  Locales,
  Reminder,
  ReminderBasedOn,
  TimeFormatOptions,
} from '@griply/types';
import { NLP } from 'shared/constants';

import {
  formatClockToken,
  parseCompactDurationToMs,
  parseTime,
} from './time-processing';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const beforeKeywords: Record<Locales, string> = {
  [Locales.EnUS]: 'before',
  [Locales.NlNL]: 'voor',
  [Locales.DeDE]: 'vor',
  [Locales.EsES]: 'antes',
};

const offsetPattern = NLP.reminderOffsetPattern;
const dayAtTimePattern = `\\d+d\\s+${NLP.timePattern}`;

// The day-at-time branch must precede the bare offset, or `!1d 09:00` matches
// as `!1d` and strands the clock time as plain text.
export const getReminderPatternString = (language: Locales) =>
  `!(?:${dayAtTimePattern}|${NLP.timePattern}|${offsetPattern}(?:\\s+${beforeKeywords[language]}\\b)?)(?!\\s*-)`;

const getDayAtTimeRegex = () =>
  new RegExp(`^!(\\d+)d\\s+(${NLP.timePattern})$`, 'i');

const getClockRegex = () => new RegExp(`^!(${NLP.timePattern})$`, 'i');

const getOffsetRegex = (language: Locales) =>
  new RegExp(
    `^!(${offsetPattern})(?:\\s+${beforeKeywords[language]}\\b)?$`,
    'i',
  );

const formatOffsetUnits = (totalMs: number) => {
  const days = Math.floor(totalMs / DAY_MS);
  const hours = Math.floor((totalMs % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((totalMs % HOUR_MS) / MINUTE_MS);
  const parts = [
    days ? `${days}d` : '',
    hours ? `${hours}h` : '',
    minutes ? `${minutes}m` : '',
  ].filter(Boolean);
  return parts.length ? parts.join('') : '0m';
};

export const parseReminderToken = (
  token: string,
  language: Locales,
): Reminder | null => {
  const trimmed = token.trim();

  const dayAtTimeMatch = trimmed.match(getDayAtTimeRegex());
  if (dayAtTimeMatch) {
    const time = parseTime(dayAtTimeMatch[2]);
    if (!time) {
      return null;
    }
    return {
      id: null,
      offset: time.inMilliseconds - Number(dayAtTimeMatch[1]) * DAY_MS,
      basedOn: ReminderBasedOn.DayStart,
    };
  }

  const clockMatch = trimmed.match(getClockRegex());
  if (clockMatch) {
    const time = parseTime(clockMatch[1]);
    if (!time) {
      return null;
    }
    return {
      id: null,
      offset: time.inMilliseconds,
      basedOn: ReminderBasedOn.DayStart,
    };
  }

  const offsetMatch = trimmed.match(getOffsetRegex(language));
  if (!offsetMatch) {
    return null;
  }

  const total = parseCompactDurationToMs(offsetMatch[1]);

  return {
    id: null,
    offset: total ? -total : 0,
    basedOn: ReminderBasedOn.Start,
  };
};

// Null when the reminder has no text form — one that fires after the start
// time. No option in the reminders menu produces that.
export const formatReminderToken = (
  reminder: Reminder,
  timeFormat: TimeFormatOptions,
  language: Locales,
): string | null => {
  const { offset, basedOn } = reminder;

  if (basedOn === ReminderBasedOn.Start) {
    if (offset > 0) {
      return null;
    }
    return `!${formatOffsetUnits(-offset)} ${beforeKeywords[language]}`;
  }

  const dayIndex = Math.floor(offset / DAY_MS);
  const clock = formatClockToken(offset - dayIndex * DAY_MS, timeFormat);
  return dayIndex ? `!${-dayIndex}d ${clock}` : `!${clock}`;
};
